import AiProvenanceNote from "../AiProvenanceNote";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Input, Modal, Progress, Select, message } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { apiConfig } from "../../authConfig";

const { TextArea } = Input;

const DOMAIN_FILTERS = [
  { key: "all", label: "All" },
  { key: "unanswered", label: "Unanswered" },
  { key: "Organization", label: "Organization" },
  { key: "IT / IT enabled", label: "IT / IT Enabled" },
  { key: "People", label: "People" },
  { key: "Physical", label: "Physical" },
];

const DOMAIN_COLORS = {
  Organization: "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]",
  "IT / IT enabled": "bg-[color:var(--color-info-bg)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
  People: "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]",
  Physical: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
};

const DOMAIN_OPTIONS = [
  { value: "Organization", label: "Organization" },
  { value: "IT / IT enabled", label: "IT / IT Enabled" },
  { value: "People", label: "People" },
  { value: "Physical", label: "Physical" },
];

function ResponseButton({ value, current, onClick, disabled }) {
  const configs = {
    Y: { label: "Y", icon: <CheckOutlined />, active: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-fg)]", idle: "bg-[color:var(--color-bg-white)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)] hover:bg-[color:var(--color-ok-bg)]" },
    N: { label: "N", icon: <CloseOutlined />, active: "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-fg)]", idle: "bg-[color:var(--color-bg-white)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)] hover:bg-[color:var(--color-danger-bg)]" },
    NA: { label: "NA", icon: <MinusOutlined />, active: "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-hero)] border-[color:var(--color-border-strong)]", idle: "bg-[color:var(--color-bg-white)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-alt)]" },
  };
  const c = configs[value];
  const isActive = current === value;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onClick(isActive ? null : value)}
      className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[10px] font-bold transition-all disabled:opacity-50 ${isActive ? c.active : c.idle}`}
    >
      {c.icon} {c.label}
    </button>
  );
}

/**
 * Internal Audit Questionnaire — AI generation, Y/N/NA answers, file picker.
 */
export function QuestionnaireSubTab({
  projectId,
  questionnaire,
  canEdit,
  getApiToken,
  onPackageUpdated,
  onSwitchTab,
}) {
  const [domainFilter, setDomainFilter] = useState("all");
  const [docLibrary, setDocLibrary] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customText, setCustomText] = useState("");
  const [customDomain, setCustomDomain] = useState("Organization");
  const [localQuestions, setLocalQuestions] = useState(questionnaire?.questions || []);
  const lastExternalRef = React.useRef(questionnaire?.questions);

  const questions = localQuestions;
  const hasQuestionnaire = questions.length > 0;

  useEffect(() => {
    if (questionnaire?.questions && questionnaire.questions !== lastExternalRef.current) {
      const incoming = questionnaire.questions;
      if (incoming.length !== localQuestions.length) {
        setLocalQuestions(incoming);
      }
      lastExternalRef.current = incoming;
    }
  }, [questionnaire?.questions]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/document-library`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (json.success && Array.isArray(json.data?.rows)) {
          setDocLibrary(json.data.rows);
        }
      } catch (e) { /* non-critical */ }
    })();
  }, [projectId, getApiToken]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getApiToken();
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/question-bank/versions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (json.success && Array.isArray(json.data?.versions)) {
          const sorted = json.data.versions.sort((a, b) => b.minorSeq - a.minorSeq);
          setVersions(sorted);
          if (sorted.length > 0 && !selectedVersion) setSelectedVersion(sorted[0].id);
        }
      } catch (e) { /* non-critical */ }
    })();
  }, [getApiToken]);

  const handleGenerate = useCallback(async () => {
    if (!projectId || generating) return;
    setGenerating(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/internal-audit/generate-questionnaire`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ versionId: selectedVersion || undefined }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not generate questionnaire.");
        return;
      }
      message.success("Questionnaire generated successfully.");
      (Array.isArray(json.meta?.warnings) ? json.meta.warnings : []).forEach((w) => message.warning(w, 8));
      if (json.data?.internalAuditPackage && onPackageUpdated) onPackageUpdated(json.data.internalAuditPackage);
    } catch (e) {
      message.error(e?.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  }, [projectId, selectedVersion, getApiToken, generating, onPackageUpdated]);

  const patchQuestion = useCallback((questionId, patch) => {
    if (!projectId) return;
    setLocalQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, ...patch } : q)),
    );
    getApiToken().then((token) => {
      fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/internal-audit/questionnaire/${encodeURIComponent(questionId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      })
        .then((r) => r.json().catch(() => ({})))
        .then((json) => {
          if (!json.success) message.error(json.error?.message || "Could not save.");
        })
        .catch(() => message.error("Save failed."));
    });
  }, [projectId, getApiToken]);

  const handleAddCustom = useCallback(async () => {
    if (!projectId || !customText.trim()) return;
    setBusy(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/internal-audit/questionnaire/add-custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: customText, domain: customDomain }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not add question.");
        return;
      }
      message.success("Custom question added.");
      setCustomModalOpen(false);
      setCustomText("");
      if (json.data?.internalAuditPackage && onPackageUpdated) onPackageUpdated(json.data.internalAuditPackage);
    } catch (e) {
      message.error(e?.message || "Add failed.");
    } finally {
      setBusy(false);
    }
  }, [projectId, customText, customDomain, getApiToken, onPackageUpdated]);

  const [proceedLoading, setProceedLoading] = useState(false);
  const handleProceedToFindings = useCallback(async () => {
    if (!projectId || proceedLoading) return;
    setProceedLoading(true);
    try {
      const token = await getApiToken();
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${projectId}/internal-audit/generate-findings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not generate findings.");
        return;
      }
      if (json.data?.internalAuditPackage && onPackageUpdated) onPackageUpdated(json.data.internalAuditPackage);
      if (onSwitchTab) onSwitchTab("audit_findings");
    } catch (e) {
      message.error(e?.message || "Generation failed.");
    } finally {
      setProceedLoading(false);
    }
  }, [projectId, proceedLoading, getApiToken, onPackageUpdated, onSwitchTab]);

  const filteredQuestions = useMemo(() => {
    if (domainFilter === "all") return questions;
    if (domainFilter === "unanswered") return questions.filter((q) => !q.response);
    return questions.filter((q) => q.domain === domainFilter);
  }, [questions, domainFilter]);

  const answered = questions.filter((q) => q.response).length;
  const total = questions.length;
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0;

  const docOptions = useMemo(() =>
    docLibrary.map((d) => ({ value: d.controlId, label: `${d.documentName} (${d.controlRef})` })),
    [docLibrary],
  );

  if (!hasQuestionnaire) {
    return (
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-10 shadow-sm text-center space-y-6">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:var(--color-info-bg)] text-2xl text-[color:var(--color-accent)]">
          <ThunderboltOutlined />
        </div>
        <div>
          <h3 className="text-lg font-black text-[color:var(--color-text-hero)]">Generate Audit Questionnaire</h3>
          <p className="mt-2 text-sm text-[color:var(--color-text-muted)] max-w-lg mx-auto">
            AI will pick 25 questions from the Question Bank and reframe them for your audit plan context and implemented controls.
          </p>
        </div>
        {versions.length > 0 && (
          <div className="flex items-center justify-center gap-3">
            <span className="text-xs font-bold text-[color:var(--color-text-muted)] uppercase tracking-wide">Question Bank Version:</span>
            <Select
              className="min-w-[160px]"
              value={selectedVersion}
              onChange={setSelectedVersion}
              options={versions.map((v) => ({ value: v.id, label: `v${v.label} (${v.totalQuestions} questions)` }))}
            />
          </div>
        )}
        <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          loading={generating}
          onClick={handleGenerate}
          className="px-8"
        >
          Generate Questions with AI
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-black tracking-tight text-[color:var(--color-text-hero)]">Audit Questionnaire</h3>
            <p className="text-xs text-[color:var(--color-text-muted)] mt-1">
              {answered} of {total} questions answered
              {questionnaire?.versionLabel && (
                <span className="ml-2 rounded-full bg-[color:var(--color-bg-alt)] border border-[color:var(--color-border)] px-2 py-0.5 text-[9px] font-bold text-[color:var(--color-text-muted)] uppercase">
                  Bank v{questionnaire.versionLabel}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Progress type="circle" percent={percent} size={48} strokeWidth={8} />
            {canEdit && (
              <Button icon={<PlusOutlined />} onClick={() => setCustomModalOpen(true)}>
                Add Custom
              </Button>
            )}
          </div>
        </div>

        {questionnaire?.aiProvenance ? (
          <div className="mb-4" data-testid="questionnaire-provenance">
            <AiProvenanceNote provenance={questionnaire.aiProvenance} />
          </div>
        ) : null}
        {questionnaire?.generation?.paddedFromQuestionBank > 0 ? (
          <div data-testid="questionnaire-padded-notice" className="mb-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-xs font-medium text-[color:var(--color-warn-fg)]">
            The AI returned {questionnaire.generation.aiQuestions} usable question{questionnaire.generation.aiQuestions === 1 ? "" : "s"}; {questionnaire.generation.paddedFromQuestionBank} more
            {questionnaire.generation.paddedFromQuestionBank === 1 ? " was" : " were"} added verbatim from the question bank (marked "Question bank, not AI").
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-1 rounded-full bg-[color:var(--color-bg-alt)] p-1 mb-6">
          {DOMAIN_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setDomainFilter(f.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-wide transition-all ${
                domainFilter === f.key ? "bg-[color:var(--color-bg-white)] text-[color:var(--color-text-hero)] shadow-sm" : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)]"
              }`}
            >
              {f.label}
              {f.key === "unanswered" && total - answered > 0 && (
                <span className="ml-1 text-[9px] text-[color:var(--color-warn-fg)]">({total - answered})</span>
              )}
            </button>
          ))}
        </div>

        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          {filteredQuestions.length === 0 && (
            <Empty description={domainFilter === "unanswered" ? "All questions answered!" : "No questions in this category."} />
          )}
          {filteredQuestions.map((q, i) => (
            <div
              key={q.id}
              className={`rounded-xl border p-4 transition-all ${
                q.response ? "bg-[color:var(--color-surface-sunken)] border-[color:var(--color-border)]" : "bg-[color:var(--color-bg-white)] border-[color:var(--color-border)] hover:border-[color:var(--color-info-line)]"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`mt-0.5 shrink-0 inline-flex rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    DOMAIN_COLORS[q.domain] || DOMAIN_COLORS.Organization
                  }`}
                >
                  {q.domain || "Organization"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[color:var(--color-text-hero)] leading-relaxed font-medium">{q.question}</p>
                  {q.source === "question_bank" && (
                    <span data-testid="question-source-bank" className="mt-1 mr-2 inline-block rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-bg-alt)] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                      Question bank, not AI
                    </span>
                  )}
                  {q.isoControl && (
                    <span className="mt-1 inline-block text-[9px] font-bold text-[color:var(--color-accent)] uppercase tracking-wider">
                      ISO {q.isoControl}
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div className="flex shrink-0 items-center gap-1.5">
                    {["Y", "N", "NA"].map((v) => (
                      <ResponseButton
                        key={v}
                        value={v}
                        current={q.response}
                        disabled={false}
                        onClick={(val) => patchQuestion(q.id, { response: val || "" })}
                      />
                    ))}
                  </div>
                )}
                {!canEdit && q.response && (
                  <span className={`shrink-0 rounded-lg border px-3 py-1 text-xs font-bold ${
                    q.response === "Y" ? "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]" :
                    q.response === "N" ? "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)]" :
                    "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]"
                  }`}>
                    {q.response}
                  </span>
                )}
              </div>

              {canEdit && q.response && (
                <div className="mt-3 ml-0 space-y-2">
                  <TextArea
                    placeholder="Add notes or remarks (optional)..."
                    defaultValue={q.notes || ""}
                    rows={2}
                    className="text-xs"
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val !== (q.notes || "")) patchQuestion(q.id, { notes: val });
                    }}
                  />
                  <Select
                    mode="multiple"
                    placeholder="Attach implemented documents..."
                    value={q.attachedDocIds || []}
                    options={docOptions}
                    className="w-full"
                    maxTagCount={2}
                    onChange={(ids) => patchQuestion(q.id, { attachedDocIds: ids })}
                  />
                </div>
              )}

              {!canEdit && q.notes && (
                <p className="mt-2 text-xs text-[color:var(--color-text-muted)] italic ml-0">{q.notes}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {canEdit && answered === total && total > 0 && (
        <div className="flex justify-end">
          <Button
            type="primary"
            size="large"
            icon={<ArrowRightOutlined />}
            loading={proceedLoading}
            onClick={handleProceedToFindings}
            className="px-8"
          >
            Proceed to Audit Findings
          </Button>
        </div>
      )}

      <Modal
        title="Add Custom Audit Question"
        open={customModalOpen}
        onCancel={() => setCustomModalOpen(false)}
        onOk={handleAddCustom}
        okText="Add Question"
        confirmLoading={busy}
      >
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Question *</label>
            <TextArea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Enter your custom audit question..."
              rows={3}
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-[color:var(--color-text-muted)] mb-1">Domain</label>
            <Select className="w-full" value={customDomain} onChange={setCustomDomain} options={DOMAIN_OPTIONS} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default QuestionnaireSubTab;
